/**
 * @engine/vfx — Graph-variable slot resolution (single source of truth).
 *
 * A graph variable lives in a packed uniform slot; its index is its position
 * among variables of the same PACKING CATEGORY, in declaration order. The
 * shader codegen (`Get Variable` node) and every runtime writer (component
 * overrides, name-addressed setters) MUST agree on this mapping — resolve it
 * only through this module.
 *
 * Categories (see VFXVariables struct):
 * - float:  16 slots — `float` and `int` variables (ints ride float slots)
 * - vec2:    8 slots
 * - vec3:    8 slots (padded to float4)
 * - color:   8 slots — `color` and `vec4`
 * - texture: 8 slots — `texture2d` and `pointmap` (group-1 texture bindings)
 */

import type { VFXGraphVariable } from '../../types';

export type VariableSlotCategory = 'float' | 'vec2' | 'vec3' | 'color' | 'texture';

/** Packing category for a variable type (null = not slot-addressable). */
export function variableCategory(type: string): VariableSlotCategory | null {
  switch (type) {
    case 'float':
    case 'int':
      return 'float';
    case 'vec2':
      return 'vec2';
    case 'vec3':
      return 'vec3';
    case 'color':
    case 'vec4':
      return 'color';
    case 'texture2d':
    case 'pointmap':
      return 'texture';
    default:
      return null;
  }
}

export interface ResolvedVariableSlot {
  readonly variable: VFXGraphVariable;
  readonly category: VariableSlotCategory;
  /** Index within the category (declaration order). */
  readonly slot: number;
}

/**
 * Resolve a variable (by id or name) to its packed slot.
 * Returns null for unknown variables or non-slot-addressable types.
 */
export function resolveVariableSlot(
  variables: readonly VFXGraphVariable[],
  ref: string
): ResolvedVariableSlot | null {
  const variable = variables.find(v => v.id === ref || v.name === ref);
  if (!variable) return null;
  const category = variableCategory(variable.type);
  if (!category) return null;

  let slot = 0;
  for (const v of variables) {
    if (v.id === variable.id) break;
    if (variableCategory(v.type) === category) slot++;
  }
  return { variable, category, slot };
}
